import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Phone, ShieldCheck, Sparkles, KeyRound } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import { changePhoneNumberAPI, changePhoneNumberOtpAPI } from '../../../Redux/ApiCalls/Auth/user'
import { showSuccessToast } from '../../atoms/Utlis/Toast'
import { removeOrderId } from '../../../Redux/Slice/Dashboard/ExtraSlice'
import { setUserPhoneNumber } from '../../../Redux/Slice/Auth/AuthSlice'
import LoadingDots from '../../atoms/Utlis/LoadinDots'
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroRow,
  HeroLeft,
  BackButton,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  ScrollBody,
  FormCard,
  CardHeader,
  CardIcon,
  CardTitle,
  CardSubtitle,
  FieldFull,
  Label,
  Input,
  PhoneRow,
  CountryCodeBox,
  PhoneInput,
  ErrorSpan,
  SubmitBar,
  SubmitHint,
  SubmitButton,
  gradient,
  FormLabelIcon,
  FormGradientDefs,
} from '../../molecules/Dashboard/Member/memberFormStyles'

const StepsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const StepPill = styled.span<{ $active?: boolean; $done?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) =>
    p.$done
      ? 'rgba(49, 166, 106, 0.12)'
      : p.$active
        ? 'rgba(201, 77, 170, 0.12)'
        : 'rgba(23, 23, 23, 0.05)'};
  color: ${(p) =>
    p.$done ? '#31a66a' : p.$active ? '#c94daa' : 'rgba(23, 23, 23, 0.45)'};
  border: 1px solid
    ${(p) =>
    p.$done
      ? 'rgba(49, 166, 106, 0.2)'
      : p.$active
        ? 'rgba(201, 77, 170, 0.2)'
        : 'rgba(23, 23, 23, 0.06)'};
`

const StepDot = styled.span<{ $active?: boolean; $done?: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) =>
    p.$done ? '#31a66a' : p.$active ? '#c94daa' : 'rgba(23, 23, 23, 0.2)'};
`

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(53, 183, 200, 0.08);
  border: 1px solid rgba(53, 183, 200, 0.16);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(23, 23, 23, 0.72);
`

const FieldsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const ChangePhoneNumber: React.FC = () => {
  const [currentNumber, setCurrentNumber] = useState('')
  const [newNumber, setNewNumber] = useState('')
  const [changeNumberButton, setChangeNumberButton] = useState(false)
  const [sendOTPButton, setSendOTPButton] = useState(false)
  const [showError, setShowError] = useState(false)
  const [otp, setOtp] = useState('')
  const { loading, isError, error, isPhoneNumberChange, changePhonenumberOrderId } =
    useAppSelector((state) => state.extra)
  const { user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (user?.role === 4) {
      navigate('/dashboard/user/profile', { replace: true })
    }
  }, [user, navigate])

  useEffect(() => {
    if (isPhoneNumberChange) {
      dispatch(setUserPhoneNumber({ number: newNumber }))
      dispatch(removeOrderId())
    }
  }, [isPhoneNumberChange, isError, error, dispatch, newNumber])

  useEffect(() => {
    if (changePhonenumberOrderId) {
      showSuccessToast(`OTP sent to +91 ${newNumber}`)
    }
  }, [changePhonenumberOrderId, newNumber])

  useEffect(() => {
    validateChangeNumberButton()
    validateSendOtpButton()
  }, [newNumber, otp])

  useEffect(() => {
    if (user) {
      setCurrentNumber(user.phone_number || '')
      setNewNumber('')
      setOtp('')
      setShowError(false)
    }
    return () => {
      dispatch(removeOrderId())
    }
  }, [user, dispatch])

  const validateChangeNumberButton = () => {
    setChangeNumberButton(handleValidateOTP() && validatePhoneNumber())
  }

  const validateSendOtpButton = () => {
    setSendOTPButton(validatePhoneNumber())
  }

  const handleValidateOTP = () => otp.length === 4

  const validatePhoneNumber = (): boolean => /^[0-9]{10}$/.test(newNumber)

  const handleSendOTP = () => {
    if (!sendOTPButton) {
      setShowError(true)
      return
    }
    dispatch(changePhoneNumberOtpAPI({ phone_number: '+91' + newNumber, country_code: '+91' }))
    setSendOTPButton(false)
  }

  const handleSubmit = () => {
    if (!changePhonenumberOrderId) return
    if (!changeNumberButton) {
      setShowError(true)
      return
    }
    dispatch(
      changePhoneNumberAPI({
        new_phone_number: newNumber,
        new_country_code: '+91',
        otp,
        orderId: changePhonenumberOrderId,
      })
    )
  }

  const currentStep = useMemo(() => {
    if (isPhoneNumberChange) return 3
    if (changePhonenumberOrderId) return 2
    return 1
  }, [changePhonenumberOrderId, isPhoneNumberChange])

  const submitReady = changePhonenumberOrderId ? changeNumberButton : sendOTPButton

  return (
    <PageContainer>
      <FormGradientDefs />
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton type="button" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <div className="flex flex-col">
                <HeroBadge>
                  <Sparkles size={10} />
                  Account security
                </HeroBadge>
                <PageTitle className="linear-text">Change phone number</PageTitle>
                <PageSubtitle>
                  Update the mobile number linked to your account. We&apos;ll send a one-time code to verify the new number.
                </PageSubtitle>
              </div>
            </HeroText>
          </HeroLeft>
        </HeroRow>
      </Hero>

      <ScrollBody>
        <FormCard>
          <CardHeader>
            <CardIcon $tone="#35b7c8">
              <Phone size={18} />
            </CardIcon>
            <div>
              <CardTitle>Phone verification</CardTitle>
              <CardSubtitle>Enter your new number and confirm with OTP</CardSubtitle>
            </div>
          </CardHeader>

          <StepsRow>
            <StepPill $active={currentStep === 1} $done={currentStep > 1}>
              <StepDot $active={currentStep === 1} $done={currentStep > 1} />
              New number
            </StepPill>
            <StepPill $active={currentStep === 2} $done={currentStep > 2}>
              <StepDot $active={currentStep === 2} $done={currentStep > 2} />
              Verify OTP
            </StepPill>
            <StepPill $active={currentStep === 3} $done={currentStep === 3}>
              <StepDot $active={currentStep === 3} $done={currentStep === 3} />
              Updated
            </StepPill>
          </StepsRow>

          <InfoBanner>
            <ShieldCheck size={16} color="#35b7c8" style={{ flexShrink: 0, marginTop: 1 }} />
            Your current number stays active until the new number is verified successfully.
          </InfoBanner>

          <FieldsGrid>
            <FieldFull>
              <Label htmlFor="current_number">
                <FormLabelIcon icon={Phone} />
                Current phone number
              </Label>
              <Input
                readOnly
                id="current_number"
                name="current_number"
                type="text"
                value={currentNumber ? `+91 ${currentNumber}` : '—'}
              />
            </FieldFull>

            <FieldFull>
              <Label htmlFor="new_number">
                <FormLabelIcon icon={Phone} />
                New phone number
              </Label>
              <PhoneRow>
                <CountryCodeBox>
                  <span>+91</span>
                </CountryCodeBox>
                <PhoneInput
                  id="new_number"
                  name="new_number"
                  type="tel"
                  inputMode="numeric"
                  value={newNumber}
                  onChange={(e) => {
                    if (e.target.value.length <= 10) setNewNumber(e.target.value.replace(/\D/g, ''))
                  }}
                  readOnly={Boolean(changePhonenumberOrderId)}
                  placeholder="Enter 10 digit phone number"
                />
              </PhoneRow>
              {showError && !validatePhoneNumber() && (
                <ErrorSpan>Provide a valid 10-digit phone number.</ErrorSpan>
              )}
            </FieldFull>

            {changePhonenumberOrderId && (
              <FieldFull>
                <Label htmlFor="otp">
                  <FormLabelIcon icon={KeyRound} />
                  Verification code
                </Label>
                <Input
                  id="otp"
                  name="otp"
                  type="tel"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => {
                    if (e.target.value.length <= 4) {
                      setOtp(e.target.value.replace(/\D/g, ''))
                    }
                  }}
                  placeholder="Enter 4 digit OTP"
                  maxLength={4}
                />
                {showError && !handleValidateOTP() && (
                  <ErrorSpan>Provide a valid 4-digit OTP.</ErrorSpan>
                )}
              </FieldFull>
            )}
          </FieldsGrid>

          <SubmitBar>
            <SubmitHint>
              {changePhonenumberOrderId
                ? `OTP sent to +91 ${newNumber}. Enter the code to confirm your new number.`
                : 'We will send a one-time password to your new mobile number.'}
            </SubmitHint>
            {loading ? (
              <LoadingWrap>
                <LoadingDots />
              </LoadingWrap>
            ) : (
              <SubmitButton
                type="button"
                $ready={submitReady}
                onClick={changePhonenumberOrderId ? handleSubmit : handleSendOTP}
              >
                {changePhonenumberOrderId ? 'Confirm & update' : 'Send OTP'}
              </SubmitButton>
            )}
          </SubmitBar>
        </FormCard>
      </ScrollBody>
    </PageContainer>
  )
}

export default ChangePhoneNumber

const LoadingWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 48px;
  padding: 0 28px;
  border-radius: 12px;
  background: ${gradient};
`
